import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import MileageLimitOdomterTooltip from '@di-shared/features/mileage/ecomileage/MileageLimitOdomterTooltip';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';

export interface MileageChallengeDescUpdateProps {
  odometer: number;
  updatedDate: Date | null;
}

export interface MileageChallengeDescCaseProps {
  limitDistance: number;
  odometer: number;
  challengeEndDate: Date;
  updatedDate: Date | null;
  mileage: number;
}

export function MileageChallengeDescV2({odometer, limitDistance, challengeEndDate, updatedDate, mileage}: MileageChallengeDescCaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const remainingDistance = limitDistance - odometer;
  const isSuccess = remainingDistance >= 0;

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4
          className="title_xsmall_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {isSuccess
            ? t('T30235_$date_$odometer_$mileage', {
                date: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
                odometer: formatNumberWithCommas(convertDistanceValue(limitDistance)) + convertDistanceSymbol(limitDistance),
                mileage: mileage / 10000,
              })
            : t('T30236_$mileage', {mileage: mileage / 10000})}
        </h4>
      </div>
      <span className="section_container_caption label_small_r">
        {t('T30001')} : {updatedDate && CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}
      </span>
      <div className="mileage_info">
        <ul className="mileage_info_list">
          <li className="mileage_info_item">
            <div className="mileage_info_key">
              {t('T30237')}
              <MileageLimitOdomterTooltip />
            </div>
            <div className="mileage_info_value">
              {formatNumberWithCommas(convertDistanceValue(limitDistance))}
              {convertDistanceSymbol(limitDistance)}
            </div>
          </li>
          <li className="mileage_info_item">
            <div className="mileage_info_key">{t('T30238')}</div>
            <div className="mileage_info_value">
              {!isSuccess && (
                <div className="badge_box">
                  <span className="badge badge_container_red badge_20px">{t('T30242')}</span>
                </div>
              )}
              {formatNumberWithCommas(convertDistanceValue(odometer))}
              {convertDistanceSymbol(odometer)}
            </div>
          </li>
          <li className="mileage_info_item type_total">
            <div className="mileage_info_key">{t('T30239')}</div>
            <div className="mileage_info_value">
              {formatNumberWithCommas(convertDistanceValue(Math.max(remainingDistance, 0)))}
              {convertDistanceSymbol(Math.max(remainingDistance, 0))}
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
}

export function MileageChallengeDescUpdate({odometer, updatedDate}: MileageChallengeDescUpdateProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4
          className="title_xsmall_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30241')}
        </h4>
      </div>
      <span className="section_container_caption label_small_r">
        {t('T30001')} : {updatedDate && CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}
      </span>
      <div className="mileage_info">
        <ul className="mileage_info_list">
          <li className="mileage_info_item type_nodata">
            <div className="mileage_info_key">
              {t('T30237')}
              <MileageLimitOdomterTooltip />
            </div>
            <div className="mileage_info_value">{t('T30243')}</div>
          </li>

          <li className="mileage_info_item">
            <div className="mileage_info_key">{t('T30238')}</div>
            <div className="mileage_info_value">
              {formatNumberWithCommas(convertDistanceValue(odometer))}
              {convertDistanceSymbol(odometer)}
            </div>
          </li>
          <li className="mileage_info_item type_nodata type_total">
            <div className="mileage_info_key">{t('T30239')}</div>
            <div className="mileage_info_value">{t('T30243')}</div>
          </li>
        </ul>
      </div>
    </div>
  );
}

export function MileageChallengeDescError() {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4
          className="title_xsmall_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30244')}
        </h4>
      </div>
      <div className="mileage_info">
        <ul className="mileage_info_list">
          <li className="mileage_info_item type_nodata">
            <div className="mileage_info_key">
              {t('T30237')}
              <MileageLimitOdomterTooltip />
            </div>
            <div className="mileage_info_value">{t('T30245')}</div>
          </li>

          <li className="mileage_info_item type_nodata">
            <div className="mileage_info_key">{t('T30238')}</div>
            <div className="mileage_info_value">{t('T30245')}</div>
          </li>
          <li className="mileage_info_item type_nodata type_total">
            <div className="mileage_info_key">{t('T30239')}</div>
            <div className="mileage_info_value">{t('T30245')}</div>
          </li>
        </ul>
      </div>
    </div>
  );
}

export function MileageChallengeDescLoading() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol} = useUnitConverter();

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b skeleton">{t('T30244')}</h4>
      </div>
      <span className="section_container_caption label_small_r skeleton">{t('T30001')} : </span>

      <div className="mileage_info">
        <ul className="mileage_info_list">
          <li className="mileage_info_item skeleton">
            <div className="mileage_info_key">{t('T30237')}</div>
            <div className="mileage_info_value">{defaultDistanceSymbol}</div>
          </li>
          <li className="mileage_info_item skeleton">
            <div className="mileage_info_key">{t('T30238')}</div>
            <div className="mileage_info_value">{defaultDistanceSymbol}</div>
          </li>
          <li className="mileage_info_item type_total skeleton">
            <div className="mileage_info_key">{t('T30239')}</div>
            <div className="mileage_info_value">{defaultDistanceSymbol}</div>
          </li>
        </ul>
      </div>
    </div>
  );
}
